// What? Tracks which section is currently under the top of the viewport.
// Why? So the navigator can highlight where you are while you scroll.
// How? One IntersectionObserver over the [data-nav-section-id] elements, with a
//      top margin that discounts the fixed upperbar and any sticky header, so
//      "active" means "the first section visible below the chrome".

import { useEffect, useState } from "react";

import { NAV_SECTION_ATTR } from "./types";

/**
 * @param ids           Section ids to observe, in document order.
 * @param topOffsetPx   Height of fixed/sticky chrome above the content.
 */
export const useActiveSection = (
  ids: string[],
  topOffsetPx: number,
): string | null => {
  const [activeId, setActiveId] = useState<string | null>(null);

  // Join so the effect re-runs when the set of sections changes, not on every
  // render (a fresh array identity arrives with each parent render).
  const idKey = ids.join("|");

  useEffect(() => {
    if (!ids.length || typeof IntersectionObserver === "undefined") {
      setActiveId(null);
      return;
    }

    const elements = ids
      .map((id) =>
        document.querySelector<HTMLElement>(`[${NAV_SECTION_ATTR}="${id}"]`),
      )
      .filter((el): el is HTMLElement => el !== null);

    if (!elements.length) return;

    // A callback only reports the entries that changed, so track the visibility
    // of every observed element across callbacks.
    const visibility = new Map<Element, boolean>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          visibility.set(entry.target, entry.isIntersecting);
        });

        const firstVisible = elements.find((el) => visibility.get(el));
        // Keep the last known section when nothing intersects (e.g. a tall
        // section spanning the whole band, or the very bottom of the page).
        if (firstVisible) {
          setActiveId(firstVisible.getAttribute(NAV_SECTION_ATTR));
        }
      },
      {
        root: null,
        // Shrink the observation band to a strip just below the chrome.
        rootMargin: `-${topOffsetPx + 8}px 0px -70% 0px`,
        threshold: 0,
      },
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ids tracked via idKey
  }, [idKey, topOffsetPx]);

  return activeId;
};
